import { Skeleton, TextField } from '@mui/material';
import { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import FieldEditComponent from 'components/Atoms/Form/FieldEditComponent/FieldEditComponent';
import { UpdateMyCompanyInput } from 'src/graphql/generated/schema';

import { UpdateCompanyMutationFn, UpdateMyCompanyMutationFn } from '../../graphql/generated/hooks';
import { AdminCompanyDetailsQuery, MyCompanyDetailsQuery } from '../../graphql/generated/operations';

type FormInput = Pick<UpdateMyCompanyInput, 'description'>;

interface EditCompanyDescriptionProps {
  data: MyCompanyDetailsQuery['me'] | AdminCompanyDetailsQuery | undefined;
  loading: boolean;
  mutation: UpdateMyCompanyMutationFn | UpdateCompanyMutationFn;
  companyId?: string;
}

const EditCompanyDescription: FC<EditCompanyDescriptionProps> = ({ data, loading, mutation, companyId }) => {
  const { t } = useTranslation('translation');

  const { getValues, control, reset } = useForm<FormInput>({
    values: { description: data?.company.description ?? '' },
  });

  const handleSubmit = useCallback(() => {
    mutation({
      variables: { input: { companyId, description: getValues('description') } },
      update(_, { data }) {
        if (data) {
          toast.success(t('messages.success.company-description-updated'));
        }
      },
    });
  }, [companyId, getValues, mutation, t]);

  if (loading || !data) {
    return (
      <>
        <Skeleton />
        <Skeleton />
      </>
    );
  }

  return (
    <FieldEditComponent
      title={t('pages.company-details-page.description')}
      value={getValues('description')}
      onCancel={reset}
      onSave={handleSubmit}
    >
      <Controller
        control={control}
        defaultValue={data.company.description}
        name="description"
        render={({ field }) => <TextField {...field} multiline rows="6" />}
        rules={{ required: true }}
      />
    </FieldEditComponent>
  );
};

export default EditCompanyDescription;
